import { required, email, numeric } from "@vee-validate/rules";
import { defineRule, configure } from "vee-validate";

import i18n from "@/i18n";

let _publicEmailProviderListPromise = null;
function getPublicEmailProviderList() {
  if (!_publicEmailProviderListPromise) {
    _publicEmailProviderListPromise = import("@/lib/constants/publicEmailProviderList.js").then(
      (mod) => mod.publicEmailProviderList,
    );
  }
  return _publicEmailProviderListPromise;
}

const NAME_REGEX = /^[\p{L}\s'-]+$/u;

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 50;
const MAX_EMAIL_LENGTH = 254;
const MIN_DETAILS_LENGTH = 10;
const MAX_DETAILS_LENGTH = 2000;
const MAX_WEBSITE_LENGTH = 2048;

defineRule("required", required);
defineRule("email", email);
defineRule("numeric", numeric);

defineRule("name", (value) => {
  if (!value || value.length < MIN_NAME_LENGTH) {
    return i18n.global.t("feedback.validation.nameMinLength");
  }

  if (!NAME_REGEX.test(value)) {
    return i18n.global.t("feedback.validation.nameFormat");
  }

  if (value.length > MAX_NAME_LENGTH) {
    return i18n.global.t("feedback.validation.nameMaxLength");
  }

  return true;
});

defineRule("workEmail", async (value) => {
  const publicEmailProviderList = await getPublicEmailProviderList();

  if (!publicEmailProviderList.some((e) => value.toLowerCase().includes(`@${e.toLowerCase()}`)) && email(value)) {
    if (value.length > MAX_EMAIL_LENGTH) {
      return i18n.global.t("feedback.validation.emailMaxLength");
    }

    return true;
  }

  return i18n.global.t("feedback.validation.workEmail");
});

defineRule("details", (value) => {
  if (value.length < MIN_DETAILS_LENGTH) {
    return i18n.global.t("feedback.validation.detailsMinLength");
  }

  if (value.length > MAX_DETAILS_LENGTH) {
    return i18n.global.t("feedback.validation.detailsMaxLength");
  }

  return true;
});

defineRule("website", (value) => {
  const rgx = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR IP (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$", // fragment locator
    "i",
  );

  const isValid = rgx.test(value);

  if (isValid) {
    if (value.length > MAX_WEBSITE_LENGTH) {
      return i18n.global.t("feedback.validation.websiteMaxLength");
    }

    return true;
  }

  return i18n.global.t("feedback.validation.website");
});

defineRule("url", (value) => {
  const rgx = new RegExp(
    "^(https?:\\/\\/|ftp:\\/\\/|www\\.)?" + // protocol or www
      "(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,63}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3})|" + // IPv4
      "\\[([a-fA-F\\d:]+)\\]" + // IPv6
      "(\\:\\d{1,5})?" + // port (optional)
      "(\\/[\\w\\-.~%]*)*" + // path (optional)
      "(\\?[;&\\w\\-.~+=%]*)?" + // query string (optional)
      "(\\#[\\w\\-.~+=%]*)?$", // fragment (optional)
    "i",
  );

  const isValid = rgx.test(value);

  if (isValid) {
    return true;
  }

  return i18n.global.t("feedback.validation.url");
});

configure({
  generateMessage: (context) => {
    const messages = {
      required: i18n.global.t("feedback.validation.required"),
      email: i18n.global.t("feedback.validation.email"),
      numeric: i18n.global.t("feedback.validation.numeric"),
      workEmail: i18n.global.t("feedback.validation.workEmail"),
      website: i18n.global.t("feedback.validation.website"),
      url: i18n.global.t("feedback.validation.url"),
      name: i18n.global.t("feedback.validation.nameFormat"),
      details: i18n.global.t("feedback.validation.detailsMaxLength"),
      nameMinLength: i18n.global.t("feedback.validation.nameMinLength"),
      detailsMinLength: i18n.global.t("feedback.validation.detailsMinLength"),
    };

    return messages[context.rule.name];
  },
});
